import React, {useState, useContext} from 'react';
import {Dialogue, Modal, Button, Icon, Text, addToast} from '@albert-io/atomic';
import PropTypes from 'prop-types';
import './submit-assignment-button.scss';

import {submitAssignment, AssignmentContext} from '../assignmentPracticeView.utils';

const UnsafeWithoutConfirmSubmitAssignmentButton = ({
  onInit = () => {},
  onEnd = () => {},
  color = 'secondary',
  children = 'Submit Assignment',
  variant = 'solid'
}) => {
  const {assignment} = useContext(AssignmentContext);
  return (
    <Button
      color={color}
      variant={variant}
      onClick={async () => {
        onInit();
        try {
          await submitAssignment({assignment});
        } catch (e) {
          addToast({
            type: 'danger',
            title: 'Error',
            message: 'Something went wrong with your request. Please try again.'
          });
        } finally {
          onEnd();
        }
      }}
    >
      {children}
    </Button>
  );
};

UnsafeWithoutConfirmSubmitAssignmentButton.propTypes = {
  onInit: PropTypes.func,
  onEnd: PropTypes.func,
  color: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.string
};

const SubmitAssignmentButton = ({
  onInit = () => {},
  onEnd = () => {},
  onDismiss = () => {},
  as: Wrapper = Button,
  ...rest
}) => {
  const [shouldConfirm, setShouldConfirm] = useState(false);
  return (
    <>
      <Wrapper {...rest} onClick={() => setShouldConfirm(true)} />
      {shouldConfirm && (
        <ConfirmSubmitModal
          onInit={onInit}
          onEnd={onEnd}
          onDismiss={onDismiss}
          handleClose={() => setShouldConfirm(false)}
        />
      )}
    </>
  );
};

SubmitAssignmentButton.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onInit: PropTypes.func,
  onEnd: PropTypes.func,
  onDismiss: PropTypes.func
};

/**
 * @todo: Consolidate this modal with the one in ConfirmLeaveUnfinishedAssignment.react
 */

const ConfirmSubmitModal = ({handleClose, onInit, onEnd, onDismiss}) => {
  return (
    <Modal ariaLabel='Confirm assignment submission modal' handleClose={handleClose}>
      {({modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            hideCloseBtn
            className={modalContentStyle}
            handleClose={handleClose}
            alignTitle='center'
            paddingLevel={6}
          >
            <Dialogue.Title align='center' size='xs'>
              <div className='submit-assignment-button__icon-wrapper'>
                <Icon icon={['fal', 'exclamation-triangle']} />
              </div>
            </Dialogue.Title>
            <Dialogue.Body align='center' className='u-mar-t_5'>
              <Text bold as='p' className='u-mar-b_5'>
                It appears that you have not submitted answers for some questions.
              </Text>
              <Text>
                You must record your answers by clicking &quot;Submit answer&quot; for each
                question. Please go back and submit answers to any questions you want to answer.
              </Text>
            </Dialogue.Body>
            <div className='u-display_flex u-flex-direction_column u-gap_2 u-mar-t_5'>
              <Button
                onClick={() => {
                  handleClose();
                  onDismiss();
                }}
              >
                Go Back
              </Button>
              <UnsafeWithoutConfirmSubmitAssignmentButton
                color='destructive'
                variant='outlined'
                onInit={onInit}
                onEnd={() => {
                  handleClose();
                  onEnd();
                }}
              >
                Submit Assignment With Unanswered Questions
              </UnsafeWithoutConfirmSubmitAssignmentButton>
            </div>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

ConfirmSubmitModal.propTypes = {
  handleClose: PropTypes.func,
  onEnd: PropTypes.func,
  onInit: PropTypes.func,
  onDismiss: PropTypes.func
};

export default SubmitAssignmentButton;

export {UnsafeWithoutConfirmSubmitAssignmentButton};
